/**
 * Model invariants (W1-03 core-model-ops; DESIGN.md §6, contract core/model/types.ts).
 *
 * `checkInvariants(doc, idx)` is the full check (tests, after journal replay, import);
 * `checkInvariantsCached(doc, idx, cache)` is the same check over a mostly unchanged document (dev builds after
 * every transaction, UX-029): the rules of records already found valid are skipped, while every rule that reads
 * other records and every table-wide pass still runs.
 * `InvariantChecker` exposes the same checks per record so `Ops.validateTouched` (core/ops/validate.ts) can run
 * only those a transaction can have broken.
 *
 * Checked:
 *  - record shapes (types, enums, integer fields, normalised beats, no unknown fields; core/model/schema.ts);
 *  - ids: stored under their own id, numeric ids below `idSeq` and never shared between tables;
 *  - tempo: non-empty, exactly one point at beat 0, none before it, unique beats, BPM > 0;
 *  - `lineOrder` / `effectOrder` are permutations of their tables;
 *  - lines: parent exists and parent chains are acyclic, group exists, texture asset exists, control points sorted,
 *    gif tracks and `gifAutoLoop` only on GIF textures (`gifAutoLoop` only without a gif track);
 *  - tracks: owner exists and owns at most one track, layer 0 for extended properties, value type of the owner,
 *    at least one key, effect/video variables point back at their track, lead-in (first key at beat 0 unless
 *    `importedLeadIn`; the flag only while the first key is after beat 0; a first key before beat 0 only on lines
 *    with origin 'import', change request CR-W1-03-3);
 *  - keys: strictly increasing beats, first key without jump and not a gap, HOLD ARRIVES AS A JUMP (after a hold the
 *    next key's vIn equals the held vOut), no trailing jump on gif tracks, values of the track's type (colour
 *    channels integers 0..255), tween ids 1..29 with 0 <= left < right <= 1, no Bezier on speed or variables;
 *  - notes: line exists, non-holds end where they start, holds end >= start, hitsound asset and generator exist;
 *  - effects and videos: assets and attached line exist, variables unique and of their declared type;
 *  - assets: valid package paths (no leading '/', no '..', reserved names refused), unique paths;
 *  - groups: unique positions; singletons: preview range, package ranges, meter marks, meta asset references.
 *
 * Every violation carries its catalogue key (`code`), language-neutral `params` (ids, numbers, names and codes that
 * the 'model' catalogue spells out with ICU select) and the English `message` (core/model/messageFormat.ts).
 */

import { Beats } from '../time/beat';
import { hasGifTexture } from './lineKind';
import { formatModelMessage, type MessageParams } from './messageFormat';
import { EXT_PROPS, PROP_SPECS, requiresLeadInKey } from './props';
import { checkShape, RECORD_SHAPES, SINGLETON_SHAPES, TABLE_NAMES } from './schema';
import type {
  AnyId,
  AssetId,
  AssetRef,
  BpmPoint,
  ChartDoc,
  CheckInvariantsFn,
  DocIndexes,
  Effect,
  EffectVarType,
  InvariantViolation,
  Key,
  KeyId,
  Line,
  LineId,
  LineProp,
  Note,
  OrderListName,
  SingletonName,
  TableName,
  TableRecords,
  Track,
  TrackId,
  TrackValueType,
  Video,
} from './types';

export { formatModelMessage } from './messageFormat';

/** Max shape problems reported per record. */
const MAX_SHAPE_PROBLEMS = 8;

type Params = MessageParams;

/**
 * An InvariantViolation whose (contracted, CR-W1-03-2) `params` are always set, so the UI can show it in any locale
 * with `i18n.tr({ key: v.code, params: v.params })`. Parameters are language-neutral.
 */
export interface ModelViolation extends InvariantViolation {
  readonly params: Params;
}

/** Builds a violation: `code` is the full key ('model.inv.x'), `params` its parameters, `message` the English text. */
export function violation(key: string, params: Params, table?: TableName, id?: AnyId): ModelViolation {
  const base = { code: `model.${key}`, message: formatModelMessage(key, params), params };
  if (table !== undefined && id !== undefined) return { ...base, table, id };
  if (table !== undefined) return { ...base, table };
  if (id !== undefined) return { ...base, id };
  return base;
}

const NUMERIC_TABLES: readonly TableName[] = TABLE_NAMES.filter((t) => t !== 'assets');

/** Value type of the track a variable of this type owns. */
export function varValueType(t: EffectVarType): TrackValueType {
  switch (t) {
    case 'float':
      return 'number';
    case 'vec2':
      return 'vec2';
    case 'color':
      return 'rgba8';
  }
}

function isU8(x: unknown): boolean {
  return typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= 255;
}

function isFiniteNum(x: unknown): boolean {
  return typeof x === 'number' && Number.isFinite(x);
}

/** True when `v` is a valid value of a track of type `t`. */
export function isTrackValueOf(t: TrackValueType, v: unknown): boolean {
  switch (t) {
    case 'number':
      return isFiniteNum(v);
    case 'string':
      return typeof v === 'string';
    case 'rgb':
      return Array.isArray(v) && v.length === 3 && v.every(isU8);
    case 'rgba8':
      return Array.isArray(v) && v.length === 4 && v.every(isU8);
    case 'vec2':
      return Array.isArray(v) && v.length === 2 && v.every(isFiniteNum);
  }
}

/** Equality of track values (numbers by value, tuples element-wise). */
export function trackValueEquals(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' && typeof b === 'number') return a === b || (Number.isNaN(a) && Number.isNaN(b));
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => x === (b as unknown[])[i]);
  }
  return a === b;
}

const RESERVED_PATHS: ReadonlySet<string> = new Set(['export.json', 'line.png']);
const RESERVED_HITSOUNDS: ReadonlySet<string> = new Set(['tap.mp3', 'flick.mp3', 'drag.mp3']);

/** Why a package path is invalid: the `reason` selector of `model.inv.assetPath`. */
export type AssetPathProblem =
  'empty' | 'leadingSlash' | 'backslash' | 'emptyFolder' | 'dotSegment' | 'reserved' | 'clickPng' | 'builtinHitsound';

/** Every AssetPathProblem (tests check that the catalogue has a branch for each). */
export const ASSET_PATH_PROBLEMS: readonly AssetPathProblem[] = [
  'empty',
  'leadingSlash',
  'backslash',
  'emptyFolder',
  'dotSegment',
  'reserved',
  'clickPng',
  'builtinHitsound',
];

/** Why `path` is not a valid package path for an asset of `role`, or null when it is valid. */
export function assetPathProblem(path: string, role: AssetRef['role']): AssetPathProblem | null {
  if (path.length === 0) return 'empty';
  if (path.startsWith('/')) return 'leadingSlash';
  if (path.includes('\\')) return 'backslash';
  const parts = path.split('/');
  if (parts.some((p) => p.length === 0)) return 'emptyFolder';
  if (parts.some((p) => p === '..' || p === '.')) return 'dotSegment';
  if (RESERVED_PATHS.has(path)) return 'reserved';
  if (path.endsWith('click.png')) return 'clickPng';
  if (role === 'hitsound' && RESERVED_HITSOUNDS.has(path)) return 'builtinHitsound';
  return null;
}

/**
 * State a repeated check can reuse (dev builds after every transaction, UX-029). Records are immutable (a change
 * stores a new object), so what the cache remembers by object identity stays true for that object. Create one per
 * store with `createInvariantCache()`.
 */
export interface InvariantCache {
  /**
   * Records whose own rules (identity, shape, the record's local table rules) found no violation:
   * `checkInvariantsCached` skips those and re-runs only the rules that read other records (`refs`) plus the
   * table-wide passes. A record that reported anything is re-checked on every run (the dev baseline needs its
   * violations re-reported), and any changed record is a new object and is checked again.
   */
  readonly recordOk: WeakSet<object>;
  /**
   * The owner record, key-id list and key records a track's `keySequence` was last run against (identity-stable:
   * the model never mutates a record in place). The sequence rules read every key of the track, so the run is
   * skipped only while every input is the same object (`ids` catches list changes without walking; the key objects
   * catch value edits, which keep the id list).
   */
  readonly seqOk: WeakMap<object, SeqStamp>;
  /** What the shared-id pass last ran for (ids only change when a record is inserted or deleted). */
  idStamp: { readonly idSeq: number; readonly records: number } | null;
}

/** Inputs of one `keySequence` run: the owner record (lead-in depends on the line's `origin`) and the keys. */
interface SeqStamp {
  readonly owner: object | undefined;
  readonly ids: readonly KeyId[];
  /** `this.doc.keys.get(ids[i])` at the time of the run (holes allowed, as `resolveKeys` skips them). */
  readonly objs: readonly (Key | undefined)[];
}

export function createInvariantCache(): InvariantCache {
  return { recordOk: new WeakSet(), seqOk: new WeakMap(), idStamp: null };
}

/**
 * Records whose `checkShape` run found no problem (by identity, shared by every checker: the shape of a record is a
 * pure function of the record, and records never change in place). A record with shape problems is walked again
 * every time, so the walk's findings cannot be lost.
 */
const SHAPE_OK = new WeakSet();

/**
 * Per-record invariant checks over a document view and its indexes. Results accumulate in `out`; each check may be
 * called more than once for the same record only if duplicates in `out` are acceptable (callers dedupe inputs).
 */
export class InvariantChecker {
  readonly out: InvariantViolation[] = [];
  private readonly doc: ChartDoc;
  private readonly idx: DocIndexes;
  private readonly cache: InvariantCache | null;

  constructor(doc: ChartDoc, idx: DocIndexes, cache: InvariantCache | null = null) {
    this.doc = doc;
    this.idx = idx;
    this.cache = cache;
  }

  private add(key: string, params: Params, table?: TableName, id?: AnyId): void {
    this.out.push(violation(key, params, table, id));
  }

  // -------------------------------------------------------------------------------------------------------------
  // Generic record checks
  // -------------------------------------------------------------------------------------------------------------

  /** Shape of a record (types, enums, integers, normalised beats, unknown fields). Returns false on problems. */
  shape<T extends TableName>(tbl: T, rec: TableRecords[T]): boolean {
    if (SHAPE_OK.has(rec)) return true;
    const problems = checkShape(RECORD_SHAPES[tbl], rec, `${tbl}[${String(rec.id)}]`, MAX_SHAPE_PROBLEMS);
    for (const p of problems) this.add('inv.field', { ...p.params, where: p.path }, tbl, rec.id);
    if (problems.length === 0) SHAPE_OK.add(rec);
    return problems.length === 0;
  }

  /** The record is stored under its own id; numeric ids are below the counter and not used by another table. */
  identity<T extends TableName>(tbl: T, key: TableRecords[T]['id'], rec: TableRecords[T], crossTable: boolean): void {
    if (rec.id !== key) this.add('inv.idKey', { table: tbl, key: String(key), id: String(rec.id) }, tbl, rec.id);
    if (tbl === 'assets' || typeof rec.id !== 'number') return;
    if (rec.id >= this.doc.idSeq) this.add('inv.idSeq', { idSeq: this.doc.idSeq, id: rec.id, table: tbl }, tbl, rec.id);
    if (!crossTable) return;
    for (const other of NUMERIC_TABLES) {
      if (other === tbl) continue;
      if ((this.doc[other] as ReadonlyMap<number, unknown>).has(rec.id)) {
        this.add('inv.idShared', { id: rec.id, table: tbl, other }, tbl, rec.id);
      }
    }
  }

  /** Shape + identity + the table-specific check of one existing record (stored under `key`, default its id). */
  record<T extends TableName>(
    tbl: T,
    rec: TableRecords[T],
    crossTable: boolean,
    key: TableRecords[T]['id'] = rec.id,
  ): void {
    this.identity(tbl, key, rec, crossTable);
    if (!this.shape(tbl, rec)) return;
    switch (tbl) {
      case 'lines':
        this.line(rec as Line);
        break;
      case 'tracks':
        this.track(rec as Track);
        break;
      case 'keys':
        this.key(rec as Key);
        break;
      case 'notes':
        this.note(rec as Note);
        break;
      case 'effects':
        this.effect(rec as Effect);
        break;
      case 'videos':
        this.video(rec as Video);
        break;
      case 'assets':
        this.asset(rec as AssetRef);
        break;
      default:
        break;
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Tempo
  // -------------------------------------------------------------------------------------------------------------

  bpmTable(): void {
    const { bpm } = this.doc;
    if (bpm.size === 0) {
      this.add('inv.bpmEmpty', {}, 'bpm');
      return;
    }
    let atZero = 0;
    const byBeat = new Map<string, BpmPoint>();
    for (const p of bpm.values()) {
      if (!Beats.isNormal(p.t)) continue; // reported by the shape check
      const c = Beats.cmp(p.t, Beats.ZERO);
      if (c === 0) atZero++;
      else if (c < 0) this.add('inv.bpmBeforeZero', { id: p.id }, 'bpm', p.id);
      if (!(Number.isFinite(p.bpm) && p.bpm > 0))
        this.add('inv.bpmValue', { id: p.id, bpm: String(p.bpm) }, 'bpm', p.id);
      const k = Beats.key(p.t);
      const other = byBeat.get(k);
      if (other !== undefined) {
        const [lo, hi] = other.id < p.id ? [other, p] : [p, other];
        this.add('inv.bpmDuplicate', { id: lo.id, other: hi.id, beat: k }, 'bpm', hi.id);
      } else {
        byBeat.set(k, p);
      }
    }
    if (atZero !== 1) this.add('inv.bpmAtZero', { count: atZero }, 'bpm');
  }

  // -------------------------------------------------------------------------------------------------------------
  // Order lists
  // -------------------------------------------------------------------------------------------------------------

  order(list: OrderListName): void {
    const ids = this.doc[list] as readonly number[];
    const tbl: TableName = list === 'lineOrder' ? 'lines' : 'effects';
    const table = this.doc[tbl] as ReadonlyMap<number, unknown>;
    const seen = new Set<number>();
    let ok = ids.length === table.size;
    for (const id of ids) {
      if (seen.has(id) || !table.has(id)) ok = false;
      seen.add(id);
    }
    if (!ok) this.add('inv.orderPermutation', { list, table: tbl }, tbl);
  }

  // -------------------------------------------------------------------------------------------------------------
  // Lines
  // -------------------------------------------------------------------------------------------------------------

  line(l: Line): void {
    this.lineRefs(l);
    for (const key of ['pos', 'alpha', 'size', 'y'] as const) {
      const curve = l.controls[key];
      if (curve === null) continue;
      for (let i = 1; i < curve.points.length; i++) {
        const a = curve.points[i - 1];
        const b = curve.points[i];
        if (a !== undefined && b !== undefined && !(b.x >= a.x)) {
          this.add('inv.controlOrder', { id: l.id, control: key }, 'lines', l.id);
          break;
        }
      }
    }
  }

  /** Line rules that read other records (parent chain, group, texture asset, gif tracks). */
  lineRefs(l: Line): void {
    const { doc } = this;
    if (l.parent !== null) {
      if (!doc.lines.has(l.parent)) this.add('inv.lineParentMissing', { id: l.id, parent: l.parent }, 'lines', l.id);
      else if (this.inCycle(l.id)) this.add('inv.lineParentCycle', { id: l.id }, 'lines', l.id);
    }
    if (l.group !== null && !doc.groups.has(l.group))
      this.add('inv.lineGroupMissing', { id: l.id, group: l.group }, 'lines', l.id);
    if (l.texture !== null && !doc.assets.has(l.texture)) this.assetMissing('lines', l.id, 'texture', l.texture);
    const gifTexture = hasGifTexture(doc, l);
    const gifTracks = this.idx.tracksOfLine(l.id).get('gif');
    const hasGifTrack = gifTracks !== undefined && gifTracks.length > 0;
    if (hasGifTrack && !gifTexture) this.add('inv.gifTrackTexture', { id: l.id }, 'lines', l.id);
    if (l.gifAutoLoop && (!gifTexture || hasGifTrack)) this.add('inv.gifAutoLoop', { id: l.id }, 'lines', l.id);
  }

  /** True when following the parents of `id` leads back to `id`. */
  private inCycle(id: LineId): boolean {
    const limit = this.doc.lines.size;
    let cur = this.doc.lines.get(id)?.parent ?? null;
    for (let steps = 0; cur !== null && steps <= limit; steps++) {
      if (cur === id) return true;
      cur = this.doc.lines.get(cur)?.parent ?? null;
    }
    return false;
  }

  /** `field`: texture / hitsound / shader / asset (records) or music / illustration / unlockVideo (meta). */
  private assetMissing(tbl: TableName | null, id: AnyId | null, field: string, asset: AssetId): void {
    if (tbl === null || id === null) this.add('inv.assetMissingMeta', { field, asset });
    else this.add('inv.assetMissing', { table: tbl, id: String(id), field, asset }, tbl, id);
  }

  // -------------------------------------------------------------------------------------------------------------
  // Tracks and keys
  // -------------------------------------------------------------------------------------------------------------

  /** Parameters naming the owner of a track (the OWNER fragment of the catalogue). */
  private ownerParams(t: Track): Params {
    const o = t.owner;
    switch (o.kind) {
      case 'line':
        return { ownerKind: 'line', owner: o.line, prop: o.prop, layer: o.layer };
      case 'effectVar':
        return { ownerKind: 'effectVar', owner: o.effect, name: o.name };
      case 'videoVar':
        return { ownerKind: 'videoVar', owner: o.video, which: o.which };
    }
  }

  /** Expected value type of a track from its owner, or null when the owner cannot tell (missing). */
  private expectedValueType(t: Track): TrackValueType | null {
    const o = t.owner;
    switch (o.kind) {
      case 'line':
        return PROP_SPECS[o.prop].valueType;
      case 'effectVar': {
        const v = this.doc.effects.get(o.effect)?.vars.find((x) => x.name === o.name);
        return v === undefined ? null : varValueType(v.type);
      }
      case 'videoVar':
        return 'number';
    }
  }

  track(t: Track): void {
    this.trackRefs(t);
    this.keySequenceCached(t);
  }

  /**
   * Track rules that read other records (owner link, shared owner, the owner's value type) and the cheap layer rule
   * (`keySequence` runs through `keySequenceCached`).
   */
  trackRefs(t: Track): void {
    const { doc } = this;
    const o = t.owner;
    const owner = this.ownerParams(t);
    switch (o.kind) {
      case 'line':
        if (!doc.lines.has(o.line)) this.add('inv.trackOwnerMissing', { ...owner, id: t.id }, 'tracks', t.id);
        if ((EXT_PROPS as readonly string[]).includes(o.prop) && o.layer !== 0) {
          this.add('inv.trackLayer', { id: t.id, prop: o.prop, layer: o.layer }, 'tracks', t.id);
        }
        break;
      case 'effectVar': {
        const e = doc.effects.get(o.effect);
        if (e === undefined) this.add('inv.trackOwnerMissing', { ...owner, id: t.id }, 'tracks', t.id);
        else {
          const v = e.vars.find((x) => x.name === o.name);
          if (v?.value.kind !== 'track' || v.value.track !== t.id)
            this.add('inv.trackVarLink', { ...owner, id: t.id }, 'tracks', t.id);
        }
        break;
      }
      case 'videoVar': {
        const vid = doc.videos.get(o.video);
        if (vid === undefined) this.add('inv.trackOwnerMissing', { ...owner, id: t.id }, 'tracks', t.id);
        else {
          const v = vid[o.which];
          if (v.kind !== 'track' || v.track !== t.id)
            this.add('inv.trackVarLink', { ...owner, id: t.id }, 'tracks', t.id);
        }
        break;
      }
    }
    const other = this.otherTrackOfOwner(t);
    if (other !== undefined) this.add('inv.trackOwnerShared', { ...owner, id: t.id, other }, 'tracks', t.id);
    const expected = this.expectedValueType(t);
    if (expected !== null && expected !== t.valueType) {
      this.add('inv.trackValueType', { id: t.id, valueType: t.valueType, expected }, 'tracks', t.id);
    }
  }

  /** Another track with the same owner (each of the tracks sharing an owner is reported). */
  private otherTrackOfOwner(t: Track): TrackId | undefined {
    const o = t.owner;
    if (o.kind === 'line') {
      for (const id of this.idx.tracksOfLine(o.line).get(o.prop) ?? []) {
        if (id === t.id) continue;
        const other = this.doc.tracks.get(id);
        if (other?.owner.kind === 'line' && other.owner.layer === o.layer) return id;
      }
      return undefined;
    }
    // Variable tracks are few: compare owners directly.
    for (const other of this.doc.tracks.values()) {
      if (other.id === t.id) continue;
      const p = other.owner;
      if (o.kind === 'effectVar' && p.kind === 'effectVar' && p.effect === o.effect && p.name === o.name)
        return other.id;
      if (o.kind === 'videoVar' && p.kind === 'videoVar' && p.video === o.video && p.which === o.which) return other.id;
    }
    return undefined;
  }

  /** The keys of a track in index order (ids whose record is missing are skipped, as before). */
  resolveKeys(t: Track): Key[] {
    const keys: Key[] = [];
    for (const id of this.idx.keysByTrack(t.id)) {
      const k = this.doc.keys.get(id);
      if (k !== undefined) keys.push(k);
    }
    return keys;
  }

  /**
   * `keySequence` with the input stamp (`InvariantCache.seqOk`): skipped while the track's owner record, its key-id
   * list and every key are the same objects as the last run that found nothing. A key edit stores a new Key object,
   * so the sequence rules re-run for its track even when the track record itself is unchanged (UX-029).
   */
  private keySequenceCached(t: Track): void {
    const { cache, doc } = this;
    const ids = this.idx.keysByTrack(t.id);
    const owner = t.owner.kind === 'line' ? doc.lines.get(t.owner.line) : undefined;
    if (cache !== null) {
      const prev = cache.seqOk.get(t);
      if (prev !== undefined && prev.owner === owner && prev.ids === ids && prev.objs.length === ids.length) {
        let same = true;
        for (let i = 0; i < ids.length; i++) {
          if (doc.keys.get(ids[i] as KeyId) !== prev.objs[i]) {
            same = false;
            break;
          }
        }
        if (same) return;
      }
    }
    const objs: (Key | undefined)[] = new Array<Key | undefined>(ids.length);
    const keys: Key[] = [];
    for (let i = 0; i < ids.length; i++) {
      const k = doc.keys.get(ids[i] as KeyId);
      objs[i] = k;
      if (k !== undefined) keys.push(k);
    }
    const before = this.out.length;
    this.keySequence(t, keys);
    if (cache !== null && this.out.length === before) cache.seqOk.set(t, { owner, ids, objs });
  }

  /** Ordering, first-key, hold-jump and gif rules over the keys of a track, plus per-key value/ease checks. */
  keySequence(t: Track, keys: readonly Key[] = this.resolveKeys(t)): void {
    if (keys.length === 0) {
      this.add('inv.trackEmpty', { id: t.id }, 'tracks', t.id);
      return;
    }
    const o = t.owner;
    const isLine = o.kind === 'line';
    const prop: LineProp | null = isLine ? o.prop : null;
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i] as Key;
      const prev = i > 0 ? keys[i - 1] : undefined;
      this.keyValuesAndEase(t, k, prop);
      if (prev === undefined) {
        if (!trackValueEquals(k.vIn, k.vOut)) this.add('inv.keyFirstJump', { track: t.id, id: k.id }, 'keys', k.id);
        if (k.ease.kind === 'hold' && k.ease.gap === true)
          this.add('inv.keyFirstGap', { track: t.id, id: k.id }, 'keys', k.id);
        continue;
      }
      if (Beats.isNormal(k.t) && Beats.isNormal(prev.t) && !Beats.lt(prev.t, k.t)) {
        this.add('inv.keyOrder', { track: t.id, id: k.id }, 'keys', k.id);
      }
      if (prev.ease.kind === 'hold' && !trackValueEquals(k.vIn, prev.vOut)) {
        this.add('inv.keyHoldJump', { track: t.id, id: k.id }, 'keys', k.id);
      }
    }
    const firstKey = keys[0];
    const lastKey = keys[keys.length - 1];
    if (prop === 'gif' && lastKey !== undefined && !trackValueEquals(lastKey.vIn, lastKey.vOut)) {
      this.add('inv.keyLastGifJump', { track: t.id }, 'keys', lastKey.id);
    }
    if (o.kind === 'line' && requiresLeadInKey(o.prop) && firstKey !== undefined && Beats.isNormal(firstKey.t)) {
      // Lead-in: the first key is at beat 0. Exceptions: an imported lead-in (first key after beat 0, flagged), and
      // on an imported line a first key BEFORE beat 0 (RPE events may start at negative beats; the key itself
      // defines the value at beat 0). Editor lines start exactly at beat 0 (CR-W1-03-3).
      const c = Beats.cmp(firstKey.t, Beats.ZERO);
      const flagged = t.importedLeadIn === true;
      const line = this.doc.lines.get(o.line);
      const importedLine = line?.origin === 'import';
      if (c > 0 && !flagged) this.add('inv.trackLeadIn', { id: t.id }, 'tracks', t.id);
      else if (c < 0 && !flagged && line !== undefined && !importedLine)
        this.add('inv.trackLeadIn', { id: t.id }, 'tracks', t.id);
      if (c <= 0 && flagged) this.add('inv.trackImportedLeadIn', { id: t.id }, 'tracks', t.id);
    }
  }

  private keyValuesAndEase(t: Track, k: Key, prop: LineProp | null): void {
    if (!isTrackValueOf(t.valueType, k.vIn))
      this.add('inv.keyValue', { id: k.id, which: 'vIn', valueType: t.valueType }, 'keys', k.id);
    if (!isTrackValueOf(t.valueType, k.vOut))
      this.add('inv.keyValue', { id: k.id, which: 'vOut', valueType: t.valueType }, 'keys', k.id);
    const e = k.ease;
    if (e.kind === 'tween') {
      const okId = Number.isInteger(e.id) && e.id >= 1 && e.id <= 29;
      const okSlice = e.left >= 0 && e.left < e.right && e.right <= 1;
      if (!okId || !okSlice) this.add('inv.keyTween', { id: k.id, easing: String(e.id) }, 'keys', k.id);
    } else if (e.kind === 'bezier') {
      if (prop === 'speed') this.add('inv.keyBezierSpeed', { id: k.id }, 'keys', k.id);
      else if (prop === null) this.add('inv.keyBezierVar', { id: k.id }, 'keys', k.id);
    }
  }

  /** A key on its own: its track exists (the sequence rules run through `track`). */
  key(k: Key): void {
    this.keyRefs(k);
  }

  /** Key rules that read other records (the track exists). */
  keyRefs(k: Key): void {
    if (!this.doc.tracks.has(k.track)) this.add('inv.keyTrackMissing', { id: k.id, track: k.track }, 'keys', k.id);
  }

  // -------------------------------------------------------------------------------------------------------------
  // Notes, effects, videos, assets, groups
  // -------------------------------------------------------------------------------------------------------------

  note(n: Note): void {
    this.noteRefs(n);
    if (Beats.isNormal(n.t) && Beats.isNormal(n.end)) {
      if (n.kind === 'hold') {
        if (Beats.lt(n.end, n.t)) this.add('inv.noteHoldEnd', { id: n.id }, 'notes', n.id);
      } else if (!Beats.eq(n.end, n.t)) {
        this.add('inv.noteEnd', { id: n.id, kind: n.kind }, 'notes', n.id);
      }
    }
  }

  /** Note rules that read other records (line, hitsound asset, generator). */
  noteRefs(n: Note): void {
    const { doc } = this;
    if (!doc.lines.has(n.line)) this.add('inv.noteLineMissing', { id: n.id, line: n.line }, 'notes', n.id);
    if (n.hitsound?.kind === 'asset' && !doc.assets.has(n.hitsound.asset))
      this.assetMissing('notes', n.id, 'hitsound', n.hitsound.asset);
    if (n.gen !== null && !doc.generators.has(n.gen))
      this.add('inv.noteGenMissing', { id: n.id, gen: n.gen }, 'notes', n.id);
  }

  effect(e: Effect): void {
    const names = new Set<string>();
    for (const v of e.vars) {
      if (names.has(v.name)) this.add('inv.effectVarDuplicate', { id: e.id, name: v.name }, 'effects', e.id);
      names.add(v.name);
      const val = v.value;
      if (val.kind === 'const') {
        if (!isTrackValueOf(varValueType(v.type), val.v))
          this.add('inv.effectVarValue', { id: e.id, name: v.name, type: v.type }, 'effects', e.id);
      }
    }
    this.effectRefs(e);
  }

  /** Effect rules that read other records (shader asset, variable track links). */
  effectRefs(e: Effect): void {
    const { doc } = this;
    if (e.shader.kind === 'asset' && !doc.assets.has(e.shader.asset))
      this.assetMissing('effects', e.id, 'shader', e.shader.asset);
    for (const v of e.vars) {
      const val = v.value;
      if (val.kind === 'track') {
        const t = doc.tracks.get(val.track);
        if (t?.owner.kind !== 'effectVar' || t.owner.effect !== e.id || t.owner.name !== v.name) {
          this.add('inv.varTrack', { table: 'effects', id: e.id, name: v.name, track: val.track }, 'effects', e.id);
        }
      }
    }
  }

  video(v: Video): void {
    this.videoRefs(v);
  }

  /** Video rules that read other records (asset, attached line, variable track links). */
  videoRefs(v: Video): void {
    const { doc } = this;
    if (!doc.assets.has(v.asset)) this.assetMissing('videos', v.id, 'asset', v.asset);
    if (v.attachLine !== null && !doc.lines.has(v.attachLine))
      this.add('inv.videoLineMissing', { id: v.id, line: v.attachLine }, 'videos', v.id);
    for (const which of ['alpha', 'dim'] as const) {
      const val = v[which];
      if (val.kind !== 'track') continue;
      const t = doc.tracks.get(val.track);
      if (t?.owner.kind !== 'videoVar' || t.owner.video !== v.id || t.owner.which !== which) {
        this.add('inv.varTrack', { table: 'videos', id: v.id, name: which, track: val.track }, 'videos', v.id);
      }
    }
  }

  asset(a: AssetRef): void {
    const reason = assetPathProblem(a.path, a.role);
    if (reason !== null) this.add('inv.assetPath', { id: a.id, path: a.path, reason }, 'assets', a.id);
  }

  /** Two assets with the same package path (they would overwrite each other in the package). */
  assetPaths(): void {
    const byPath = new Map<string, AssetId>();
    for (const a of this.doc.assets.values()) {
      const other = byPath.get(a.path);
      if (other !== undefined) this.add('inv.assetPathDuplicate', { id: a.id, other, path: a.path }, 'assets', a.id);
      else byPath.set(a.path, a.id);
    }
  }

  groups(): void {
    const byOrder = new Map<number, number>();
    const sorted = [...this.doc.groups.values()].sort((a, b) => a.id - b.id);
    for (const g of sorted) {
      const other = byOrder.get(g.order);
      if (other !== undefined) this.add('inv.groupOrder', { id: other, other: g.id, order: g.order }, 'groups', g.id);
      else byOrder.set(g.order, g.id);
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Singletons
  // -------------------------------------------------------------------------------------------------------------

  singleton(key: SingletonName): void {
    const value = this.doc[key];
    const problems = checkShape(SINGLETON_SHAPES[key], value, key, MAX_SHAPE_PROBLEMS);
    for (const p of problems) this.add('inv.field', { ...p.params, where: p.path });
    if (problems.length > 0) return;
    switch (key) {
      case 'meta': {
        const m = this.doc.meta;
        if (!(m.previewStart >= 0) || (m.previewEnd !== null && !(m.previewEnd > m.previewStart)))
          this.add('inv.previewRange', {});
        for (const field of ['music', 'illustration', 'unlockVideo'] as const) {
          const a = m[field];
          if (a !== null && !this.doc.assets.has(a)) this.assetMissing(null, null, field, a);
        }
        break;
      }
      case 'pkg': {
        const p = this.doc.pkg;
        const bad = (field: string, value: number): void => {
          this.add('inv.pkgRange', { field, value: String(value) });
        };
        if (!(p.aspectRatio > 0)) bad('aspectRatio', p.aspectRatio);
        if (!(p.backgroundDim >= 0 && p.backgroundDim <= 1)) bad('backgroundDim', p.backgroundDim);
        if (!(p.lineLength > 0)) bad('lineLength', p.lineLength);
        if (!(p.speedBakeTolPx > 0)) bad('speedBakeTolPx', p.speedBakeTolPx);
        if (!(p.rpeVersion >= 0)) bad('rpeVersion', p.rpeVersion);
        break;
      }
      case 'timing': {
        const marks = this.doc.timing.meter;
        let ok = true;
        for (let i = 0; i < marks.length; i++) {
          const m = marks[i];
          if (m === undefined) continue;
          if (!(m.beatsPerBar > 0) || Beats.cmp(m.t, Beats.ZERO) < 0) ok = false;
          const prev = i > 0 ? marks[i - 1] : undefined;
          if (prev !== undefined && !Beats.lt(prev.t, m.t)) ok = false;
        }
        if (!ok) this.add('inv.meterOrder', {});
        break;
      }
      case 'editor':
      case 'rpeRoot':
        break;
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // References to deleted records (fast path)
  // -------------------------------------------------------------------------------------------------------------

  /** Records still referring to a record that no longer exists. */
  deleted<T extends TableName>(tbl: T, rec: TableRecords[T]): void {
    const { doc, idx } = this;
    const dangling = (what: string, ref: number | string): void => {
      this.add('inv.dangling', { table: tbl, id: String(rec.id), what, ref: String(ref) }, tbl, rec.id);
    };
    switch (tbl) {
      case 'lines': {
        const id = rec.id as LineId;
        for (const n of idx.notesByLine(id)) dangling('note', n);
        for (const ts of idx.tracksOfLine(id).values()) for (const t of ts) dangling('track', t);
        for (const c of idx.childrenOf(id)) dangling('line', c);
        for (const v of doc.videos.values()) if (v.attachLine === id) dangling('video', v.id);
        break;
      }
      case 'tracks': {
        const t = rec as Track;
        for (const k of idx.keysByTrack(t.id)) dangling('key', k);
        const o = t.owner;
        if (o.kind === 'effectVar') {
          const e = doc.effects.get(o.effect);
          const v = e?.vars.find((x) => x.name === o.name);
          if (e !== undefined && v?.value.kind === 'track' && v.value.track === t.id) dangling('effect', e.id);
        } else if (o.kind === 'videoVar') {
          const vid = doc.videos.get(o.video);
          const v = vid?.[o.which];
          if (vid !== undefined && v?.kind === 'track' && v.track === t.id) dangling('video', vid.id);
        }
        break;
      }
      case 'effects':
      case 'videos': {
        // Every track owned by the deleted effect or video, also several sharing one variable (the owner index
        // knows one track per owner) and tracks naming a variable the effect does not have.
        for (const t of doc.tracks.values()) {
          const o = t.owner;
          const owned =
            tbl === 'effects'
              ? o.kind === 'effectVar' && o.effect === rec.id
              : o.kind === 'videoVar' && o.video === rec.id;
          if (owned) dangling('track', t.id);
        }
        break;
      }
      case 'assets': {
        const u = idx.assetUsers(rec.id as AssetId);
        for (const l of u.lines) dangling('line', l);
        for (const n of u.notes) dangling('note', n);
        for (const e of u.effects) dangling('effect', e);
        for (const v of u.videos) dangling('video', v);
        for (const m of u.meta) dangling('meta', m);
        break;
      }
      case 'groups': {
        for (const l of doc.lines.values()) if (l.group === rec.id) dangling('line', l.id);
        break;
      }
      case 'generators': {
        for (const n of doc.notes.values()) if (n.gen === rec.id) dangling('note', n.id);
        break;
      }
      default:
        break;
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Full check
  // -------------------------------------------------------------------------------------------------------------

  /**
   * Only the rules of `rec` that read other records (`*Refs`). A cached run re-runs these for every record (they are
   * a few map lookups) and the record's own rules only when it is new, changed or previously reported something, so
   * a reference that breaks under an unchanged record (a deleted line under an untouched note, a second track taking
   * an owner) is still reported. A track's `keySequence` is not part of this: it is stamped per input set.
   */
  refs<T extends TableName>(tbl: T, rec: TableRecords[T]): void {
    switch (tbl) {
      case 'lines':
        this.lineRefs(rec as Line);
        break;
      case 'tracks':
        this.trackRefs(rec as Track);
        break;
      case 'keys':
        this.keyRefs(rec as Key);
        break;
      case 'notes':
        this.noteRefs(rec as Note);
        break;
      case 'effects':
        this.effectRefs(rec as Effect);
        break;
      case 'videos':
        this.videoRefs(rec as Video);
        break;
      default:
        break;
    }
  }

  all(): void {
    const { doc, cache } = this;
    // Ids shared between tables: one pass over every numeric table. Skipped on a cached run whose id set cannot
    // have changed (idSeq only grows when a record is inserted; a pure edit keeps every table's size). An insert
    // with a reused id of another table is caught by `Ops.validateTouched` (per-record cross-table check) and by
    // the next pass here.
    let records = 0;
    for (const tbl of TABLE_NAMES) records += (doc[tbl] as ReadonlyMap<unknown, unknown>).size;
    const stamp = cache?.idStamp ?? null;
    if (cache === null || stamp === null || stamp.idSeq !== doc.idSeq || stamp.records !== records) {
      const before = this.out.length;
      const owner = new Map<number, TableName>();
      for (const tbl of NUMERIC_TABLES) {
        for (const id of (doc[tbl] as ReadonlyMap<number, unknown>).keys()) {
          const other = owner.get(id);
          if (other !== undefined) this.add('inv.idShared', { id, table: tbl, other }, tbl, id as AnyId);
          else owner.set(id, tbl);
        }
      }
      // While a shared id is present the pass must re-report it on every run (the baseline needs it back), so the
      // stamp is stored only for a clean pass.
      if (cache !== null && this.out.length === before) cache.idStamp = { idSeq: doc.idSeq, records };
    }
    for (const tbl of TABLE_NAMES) {
      const table = doc[tbl] as ReadonlyMap<TableRecords[TableName]['id'], TableRecords[TableName]>;
      for (const [key, rec] of table) {
        if (cache !== null && cache.recordOk.has(rec)) {
          this.refs(tbl, rec);
          continue;
        }
        const before = this.out.length;
        this.record(tbl, rec, false, key);
        if (cache !== null && this.out.length === before) cache.recordOk.add(rec);
      }
    }
    if (cache !== null) {
      // Cached tracks keep their stamped key sequence: re-run it when a key, the track or the owner line changed.
      for (const t of doc.tracks.values()) {
        if (cache.recordOk.has(t)) this.keySequenceCached(t);
      }
    }
    this.bpmTable();
    this.assetPaths();
    this.groups();
    this.order('lineOrder');
    this.order('effectOrder');
    for (const s of ['meta', 'pkg', 'timing', 'editor', 'rpeRoot'] as const) this.singleton(s);
  }
}

export const checkInvariants: CheckInvariantsFn = (doc, idx) => {
  const c = new InvariantChecker(doc, idx);
  c.all();
  return c.out;
};

/**
 * The full check over a mostly unchanged document (UX-029: the dev-build check after every transaction on large
 * charts): the same violations as `checkInvariants` (records are never mutated in place — a change stores a new
 * object), with `cache` (`createInvariantCache`, one per store) skipping the rules of records already found valid.
 * Every rule that reads other records (`refs`) and every table-wide pass still runs, so a reference that breaks
 * under an unchanged record is reported here too; `keySequence` is re-run whenever any of its keys changed.
 */
export function checkInvariantsCached(
  doc: Parameters<CheckInvariantsFn>[0],
  idx: Parameters<CheckInvariantsFn>[1],
  cache: InvariantCache,
): InvariantViolation[] {
  const c = new InvariantChecker(doc, idx, cache);
  c.all();
  return c.out;
}

/** Ids of the keys of a track in index order (helper for callers that need records). */
export function trackKeys(doc: ChartDoc, idx: DocIndexes, track: TrackId): Key[] {
  const out: Key[] = [];
  for (const id of idx.keysByTrack(track)) {
    const k = doc.keys.get(id);
    if (k !== undefined) out.push(k);
  }
  return out;
}
